import React, { useEffect } from "react";
import { CheckCircle2 } from "lucide-react";
import Navbar from "../Navbar";
import WhyChooseMe from "../WhyChooseMe";
import { Benefits } from "../Feature";
import { CTASection, Footer } from "../CTAFooter";
import { aboutContent, trustStats, logos, SITE } from "../../data/mock";
import { Reveal } from "../ui/Reveal";

const About = () => {
  useEffect(() => {
    const prevTitle = document.title;
    document.title = `${aboutContent.intro.heading} | ${SITE.name}`;
    return () => {
      document.title = prevTitle;
    };
  }, []);
  const { intro, drives, whatYouGet, industries, closing, logosTagline } = aboutContent;

  return (
    <div className="min-h-screen bg-white font-sans text-neutral-900">
      <Navbar />

      {/* Intro */}
      <section className="pt-16 pb-10">
        <Reveal>
          <div className="max-w-[900px] mx-auto px-6 text-center">
            <span className="inline-flex items-center gap-2 text-[11px] tracking-[0.18em] uppercase text-neutral-500 border border-black/10 rounded-full px-3 py-1.5 bg-white/60">
              <span className="w-1.5 h-1.5 rounded-full bg-black"></span> About
            </span>
            <h1 className="mt-6 text-[32px] sm:text-[48px] leading-[1.08] tracking-[-0.02em] font-semibold text-neutral-950">
              {intro.heading}
            </h1>
            <p className="mt-6 text-[17px] text-neutral-600 max-w-2xl mx-auto">
              {intro.body}
            </p>
          </div>
        </Reveal>
      </section>

      {/* What drives my work */}
      <section className="py-16 bg-neutral-50">
        <div className="max-w-[1000px] mx-auto px-6">
          <Reveal>
            <h2 className="text-[26px] sm:text-[36px] leading-[1.1] tracking-[-0.02em] font-semibold text-neutral-950">
              {drives.heading}
            </h2>
            <p className="mt-4 text-[18px] font-medium text-neutral-950">{drives.lead}</p>
            <p className="mt-3 text-neutral-600 max-w-3xl">{drives.body}</p>
          </Reveal>

          <div className="mt-10 grid grid-cols-1 md:grid-cols-2 gap-5">
            {whatYouGet.map((item, i) => (
              <Reveal key={item.title} delay={i * 0.08}>
                <div className="flex gap-3 bg-white rounded-2xl p-5 border border-black/5 h-full">
                  <CheckCircle2 className="w-5 h-5 text-neutral-800 flex-shrink-0 mt-0.5" />
                  <div>
                    <div className="font-semibold text-neutral-950">{item.title}</div>
                    <div className="text-sm text-neutral-600 mt-1">{item.desc}</div>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>

          <Reveal delay={0.1}>
            <div className="mt-10">
              <div className="text-sm font-semibold text-neutral-950 mb-3">Industries I've Scaled</div>
              <div className="flex flex-wrap gap-2">
                {industries.map((ind) => (
                  <span
                    key={ind}
                    className="text-sm text-neutral-800 border border-black/10 rounded-full px-4 py-2 bg-white"
                  >
                    {ind}
                  </span>
                ))}
              </div>
            </div>

            <p className="mt-10 text-lg font-medium text-neutral-950">{closing}</p>
          </Reveal>
        </div>
      </section>

      <WhyChooseMe />

      {/* Stats */}
      <section className="py-20">
        <div className="max-w-[1280px] mx-auto px-6">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-10 text-center">
            {trustStats.map((stat, i) => (
              <Reveal key={stat.label} delay={i * 0.08}>
                <div className="text-[44px] sm:text-[56px] font-semibold tracking-tight text-neutral-950 leading-none">{stat.value}</div>
                <div className="text-base text-neutral-500 mt-3">{stat.label}</div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Services (reused component) */}
      <Benefits />

      {/* Logos — infinite right-to-left marquee with a soft fade at both edges */}
      <section className="py-16 bg-white">
        <div className="max-w-[1280px] mx-auto px-6 text-center">
          <Reveal>
            <p className="text-neutral-600 max-w-xl mx-auto">{logosTagline}</p>
          </Reveal>
          <div className="mt-10 relative w-full overflow-hidden logo-marquee-mask">
            <div className="animate-marquee items-center gap-16">
              {[...logos, ...logos].map((logo, i) => (
                <img
                  key={`${logo.alt}-${i}`}
                  src={logo.src}
                  alt={logo.alt}
                  className="h-7 w-auto object-contain opacity-75 flex-shrink-0"
                />
              ))}
            </div>
          </div>
        </div>
      </section>

      <CTASection />
      <Footer />
    </div>
  );
};

export default About;
